import type { TFunction } from 'i18next';
import { COMPANY_ROLE_CODES, PROJECT_ROLE_CODES } from '@/access/roles';
import * as EitorIcon from '@/components/Icons/SceneEditorIcons';
import { CharacterImportance, CharacteristicCode, Disposition, gender, SignCharacter, Status, textAtomTypeId } from '@/enums';

declare const API_URL: string;
declare const STORAGE_URL: string;
declare const WEBSOCKET_AUTH_URL: string;
declare const WEBSOCKET_HOST: string;
declare const NODE_ENV: string;

export const STORAGE = STORAGE_URL;
export const baseUrl = API_URL;
export const WEBSOCKET_AUTH = WEBSOCKET_AUTH_URL;
export const WEBSOCKET = WEBSOCKET_HOST;
export const __ENV__ = NODE_ENV;

export const languages = [
    {
        title: 'Русский',
        value: 'ru',
        icon: '/images/ru.png',
    },
    {
        title: 'English',
        value: 'en',
        icon: '/images/us.png',
    },
];
export const paths = {
    profile: {
        index: '/',
        edit: 'profile/edit',
    },
    sign: {
        index: '/auth',
        preRegistration: '/auth/preregister',
        registration: '/auth/register',
        registrationSuccess: '/auth/register/success',
        verify: '/auth/email/verify',
        resetPassword: '/auth/reset-password',
        forgotPassword: '/auth/forgot-password',
    },
    employees: {
        index: '/employees',
        invite: '/employees/invite',
    },
    company: {
        index: '/company',
        create: '/company/create',
        success: '/company/create/success',
        projects: '/company/projects',
    },
    project: {
        index: '/project',
        create: '/project/create',
        versions: '/project/versions',
        importReports: '/project/import-reports',
        synopsis: {
            index: '/project/synopsis',
            setting: '/project/synopsis/setting',
            theme: '/project/synopsis/theme',
            references: '/project/synopsis/references',
            marketing: '/project/synopsis/marketing',
            documents: '/project/synopsis/documents',
        },
        episodes: {
            synopsis: '/project/episodes/synopsis',
            index: '/project/episodes',
        },
        character: {
            index: '/project/character',
            create: '/project/character/create',
        },
        characters: {
            index: '/project/characters',
            create: '/project/characters/create',
        },
        line: {
            index: '/project/line',
        },
        lines: {
            index: '/project/lines',
        },
        episode: {
            index: '/project/episode',
        },
        scene: {
            editor: '/project/scene/editor',
            editor_pep: '/project/scene/editor/pep',
            title: '/project/scene/titles',
        },
        plan: {
            index: '/project/plan',
        },
        location: {
            index: '/project/location',
        },
        locations: {
            index: '/project/locations',
        },
        comments: '/project/comments',
        devtools: {
            fillProject: '/project/devtools/fill-project',
        },
    },
    start: {
        index: '/start',
    },
    feedback: {
        index: '/feedback',
    },
    concept: {
        editor: '/concept/editor',
    },
};
/** Company-scope role picker items, in `GET roles` vocabulary. */
export const RolesCompany = (t: TFunction) =>
    COMPANY_ROLE_CODES.map(code => ({
        title: t(`roles.${code}`),
        value: code,
    }));
/** Project-scope role picker items. */
export const Roles = (t: TFunction) =>
    PROJECT_ROLE_CODES.map(code => ({
        title: t(`roles.${code}`),
        value: code,
    }));
export const GenderAbbreviated = (t: TFunction) => [
    { title: t('gender.mal').toUpperCase(), value: gender.male },
    { title: t('gender.fem').toUpperCase(), value: gender.female },
    { title: t('gender.oth').toUpperCase(), value: gender.other },
];
export const ProjectStatus = (t: TFunction) => [
    { title: t('project.created'), value: Status.created },
    { title: t('project.negotiation'), value: Status.negotiation },
    { title: t('project.approved'), value: Status.approved },
];
export const EpisodePlanMenu = (t: TFunction) => [
    { title: t('plan.line'), value: '2' },
    { title: t('plan.acts'), value: '1' },
];
export const CommentSwitchMenu = (t: TFunction) => [
    { title: t('comment.active'), value: '1', checked: true },
    { title: t('comment.resolved'), value: '2', checked: false },
];
export const createAct = (value: number) => ({
    value,
    typeId: textAtomTypeId.episode_act_logline,
    id: String(value),
});
export const episodeActs = [{ value: 99, typeId: textAtomTypeId.episode_act_tbd, id: 'TBD' }];
export const getActTitle = (t: TFunction, act?: (typeof episodeActs)[number]) =>
    act ? `${act.typeId === textAtomTypeId.episode_act_logline ? `${t('act.act')} ${act.id}` : 'TBD'}` : 'TBD';
export const SignCharacterMenu = (t: TFunction) => [
    { title: t('characters.antagonist'), value: SignCharacter.antagonist },
    { title: t('characters.minor'), value: SignCharacter.secondary },
    { title: t('characters.protagonist'), value: SignCharacter.protagonist },
    { title: t('characters.ally'), value: SignCharacter.ally },
    {
        title: t('characters.hidden-antagonist'),
        value: SignCharacter.hiddenAntagonist,
    },
    {
        title: t('characters.ally-antagonist'),
        value: SignCharacter.allyAntagonist,
    },
    { title: t('characters.neutral'), value: SignCharacter.neutral },
];
export const CharacterImportanceMenu = (t: TFunction) => [
    { title: t('characters.importance.main'), value: CharacterImportance.main },
    { title: t('characters.importance.secondary'), value: CharacterImportance.secondary },
    { title: t('characters.importance.episodic'), value: CharacterImportance.episodic },
    { title: t('characters.importance.extra'), value: CharacterImportance.extra },
];
export const EditorActionMenu = (t: TFunction) => [
    {
        title: t('scene.scene'),
        icon: EitorIcon.SceneSvg,
        value: 1,
        children: [
            { title: t('scene.title'), icon: EitorIcon.SceneTitleSvg, value: 1 },
            {
                title: t('scene.characters'),
                icon: EitorIcon.SceneCharactersSvg,
                value: 2,
            },
            { title: t('logline'), icon: EitorIcon.SceneLoglineSvg, value: 3 },
        ],
    },
    {
        title: t('action'),
        icon: EitorIcon.SceneActionSvg,
        value: textAtomTypeId.scene_action,
    },
    {
        title: t('dialogue'),
        icon: EitorIcon.SceneDialogueSvg,
        value: textAtomTypeId.scene_dialogue,
        children: [
            {
                title: t('character.character'),
                icon: EitorIcon.SceneDialogueCharacterSvg,
                value: 1,
            },
            {
                title: t('scene.replica'),
                icon: EitorIcon.SceneDialogueReplicaSvg,
                value: 2,
            },
            {
                title: t('scene.remark'),
                icon: EitorIcon.SceneDialogueRemarkSvg,
                value: 3,
            },
        ],
    },
    // {
    //     title: t('scene.mounting-note'),
    //     icon: EitorIcon.SceneMountingNoteSvg,
    //     value: 2,
    // },
    {
        title: t('scene.description-pep'),
        icon: EitorIcon.ScenePepSvg,
        value: 3,
    },
];
export const EditorPepActionMenu = (t: TFunction) => [
    {
        title: t('scene.scene'),
        icon: EitorIcon.SceneSvg,
        value: 1,
        children: [
            { title: t('scene.title'), icon: EitorIcon.SceneTitleSvg, value: 1 },
            {
                title: t('scene.characters'),
                icon: EitorIcon.SceneCharactersSvg,
                value: 2,
            },
            { title: t('logline'), icon: EitorIcon.SceneLoglineSvg, value: 3 },
        ],
    },
    {
        title: t('scene.description-pep'),
        icon: EitorIcon.ScenePepSvg,
        value: 2,
    },
];
export const COLORS = [
    { code: 'colorA', color: '#51A2FF' },
    { code: 'colorB', color: '#00BC7D' },
    { code: 'colorC', color: '#FF637E' },
    { code: 'colorD', color: '#A684FF' },
    { code: 'colorE', color: '#FF8904' },
    { code: 'colorF', color: '#0092B8' },
    { code: 'colorG', color: '#F6339A' },
    { code: 'colorH', color: '#FFB900' },
    { code: 'colorI', color: '#62748E' },
    { code: 'colorJ', color: '#9AE600' },
];
export const TEXT_LIMIT = {
    character: {
        target: 100,
        conflict: 100,
        character: 25,
        characterScriptName: 25,
        characterFullName: 50,
        characterAlias: 50,
        characterInitialGoal: 150,
        characterFinalGoal: 150,
        characterNickname: 150,
        characterDescription: 350,
        characterProfession: 50,
        characteristicName: 25,
        shortCharacteristic: 150,
        fullCharacteristic: 350,
        relationshipShortDescription: 150,
        relationshipFullDescription: 350,
    },
    feature: {
        name: 50,
        genre: 70,
        audience: 35,
        aspect: 35,
    },
    logline: {
        synopsis: 300,
        line: 300,
        scene: 450,
        sequence: 450,
        arc: 450,
    },
    name: {
        line: 100,
        series: 100,
        episode: 100,
        location: 25,
    },
    description: {
        location: 400,
    },
    location: {
        object: 30,
        subobject: 30,
    },
    tag: 15,
    code: 2,
    episode: {
        arch: 450,
        title: 100,
    },
};
export const ASSET_LIMIT = {
    character: 10,
    characterDreamcast: 10,
    characterReference: 30,
    characterCostumes: 30,
};
export const BasicCharacteristics = (t: TFunction) => [
    {
        name: t('character.characteristics.heroes-mask'),
        code: CharacteristicCode.mask,
        text_atoms: [
            {
                text_atom_type_id: textAtomTypeId.short_characteristic,
                content: '',
            },
            {
                text_atom_type_id: textAtomTypeId.full_characteristic,
                content: '',
            },
        ],
    },
    {
        name: t('character.characteristics.superpower'),
        code: CharacteristicCode.superpower,
        text_atoms: [
            {
                text_atom_type_id: textAtomTypeId.short_characteristic,
                content: '',
            },
            {
                text_atom_type_id: textAtomTypeId.full_characteristic,
                content: '',
            },
        ],
    },
    {
        name: t('character.characteristics.achilles-heel'),
        code: CharacteristicCode.backside,
        text_atoms: [
            {
                text_atom_type_id: textAtomTypeId.short_characteristic,
                content: '',
            },
            {
                text_atom_type_id: textAtomTypeId.full_characteristic,
                content: '',
            },
        ],
    },
    {
        name: t('character.characteristics.main-fear'),
        code: CharacteristicCode.fear,
        text_atoms: [
            {
                text_atom_type_id: textAtomTypeId.short_characteristic,
                content: '',
            },
            {
                text_atom_type_id: textAtomTypeId.full_characteristic,
                content: '',
            },
        ],
    },
    {
        name: t('character.characteristics.heroes-admiration'),
        code: CharacteristicCode.excitement,
        text_atoms: [
            {
                text_atom_type_id: textAtomTypeId.short_characteristic,
                content: '',
            },
            {
                text_atom_type_id: textAtomTypeId.full_characteristic,
                content: '',
            },
        ],
    },
    {
        name: t('character.characteristics.dark-side'),
        code: CharacteristicCode.shadow,
        text_atoms: [
            {
                text_atom_type_id: textAtomTypeId.short_characteristic,
                content: '',
            },
            {
                text_atom_type_id: textAtomTypeId.full_characteristic,
                content: '',
            },
        ],
    },
];
export const dispositionName = (t: TFunction) => {
    return {
        [Disposition.nat]: t('nature'),
        [Disposition.int]: t('interior'),
    };
};
export const searchTopPosition = {
    top: { index: 0, px: 0 },
    middle: { index: 1, history: 170, noHistory: 120, current: 100 },
    bottom: { index: 2, px: 0, top: 27 },
};
export const searchLeftPosition = {
    episode_synopsis: '530px',
    synopsis: '400px',
};
export const UPLOAD_MAX_FILESIZE = 5;
export const statements = (t: TFunction) => [
    {
        text: t('statements.hitchcock.quote'),
        author: t('statements.hitchcock.name'),
    },
    {
        text: t('statements.reynolds.quote'),
        author: t('statements.reynolds.name'),
    },
    {
        text: t('statements.fitzgerald.quote'),
        author: t('statements.fitzgerald.name'),
    },
    {
        text: t('statements.eisenstein.quote'),
        author: t('statements.eisenstein.name'),
    },
];
// Uniform five-column filter panel on every screen (#1839),
// in the layout order: Метки, Линии, Локации, Персонажи, Отношения
export const FILTER_CATEGORIES = ['custom_tags', 'lines', 'locations', 'characters', 'relationships'];
export const PAGES_FILTERS = {
    episodes: FILTER_CATEGORIES,
    planLine: FILTER_CATEGORIES,
    planEpisode: FILTER_CATEGORIES,
    lines: FILTER_CATEGORIES,
    characters: FILTER_CATEGORIES,
    locations: FILTER_CATEGORIES,
};
export const FILTERS_DEFAULT_STATE = {
    locations: [],
    episodes: [],
    characters: [],
    lines: [],
    custom_tags: [],
    relationships: [],
};
export const ALLOWED_IMG_TYPES = '.jpeg, .jpg, .png, .webp';
export const COMPANY_NAME = {
    MAX: 20,
    MIN: 2,
};

export type HintType = {
    name: string;
    isPro: boolean;
};

export type Hints = {
    [key: string]: HintType;
};

export const HINTS = {
    menu: {
        company: {
            name: 'company',
            isPro: false,
        },
        project: {
            name: 'project',
            isPro: false,
        },
        editor: {
            name: 'editor',
            isPro: false,
        },
        characters: {
            name: 'characters',
            isPro: false,
        },
        synopsis: {
            name: 'synopsis',
            isPro: false,
        },
        lines: {
            name: 'lines',
            isPro: true,
        },
        episodes: {
            name: 'episodes',
            isPro: false,
        },
        plan: {
            name: 'plan',
            isPro: false,
        },
        locations: {
            name: 'locations',
            isPro: true,
        },
        versions: {
            name: 'versions',
            isPro: false,
        },
        help: {
            name: 'help',
            isPro: false,
        },
        import: {
            name: 'import',
            isPro: false,
        },
        profile: {
            name: 'profile',
            isPro: false,
        },
    },
    head: {},
    navigator: {},
    editor: {},
    tools: {},
} satisfies Record<string, Hints>;
